import { useRef, useState } from 'react'
import type { HomeSession } from '@application/session.ts'
import type { Actor } from '@domain/events.ts'
import type { PlannedChange, Proposal } from '@domain/plan.ts'
import { nextMeetingDay, planChanges } from '@domain/plan.ts'
import type { FamilyState } from '@domain/state.ts'
import { askForPlan, type AssistantFailure } from '@adapters/ai/assistant.ts'
import { startDictado, type DictadoFailure, type Grabacion } from '@adapters/audio/dictado.ts'
import { assistantConfig, readToken, writeToken } from '@adapters/ai/config.ts'
import { es } from '../strings/es.ts'

/**
 * Configuring the house by describing it.
 *
 * The shape is the whole point, and it is the same shape as the rest of the app:
 * nothing reaches a child without an adult having read the sentence. The model
 * writes proposals, the domain refuses the ones that break a rule, and what
 * survives is a list of plain Spanish lines with a tick beside each one. Nothing
 * is applied until the parent presses the button, and what is applied goes in as
 * the parent's own decision, through the same commands as every tap.
 */

/** "2026-09-20" as "20/9", which is how a price change gets announced at home. */
function shortDay(day: string): string {
  const [, month, dayOfMonth] = day.split('-')
  return `${String(Number(dayOfMonth))}/${String(Number(month))}`
}

/** One proposal, as a sentence a parent can judge without knowing the schema. */
function describe(change: PlannedChange, dayName: string): string {
  const p = change.proposal
  const ops = es.assistant.ops
  switch (p.op) {
    case 'create-task':
      return ops['create-task'](p.child, p.name)
    case 'rename-task':
      return ops['rename-task'](p.child, p.name, p.newName)
    case 'set-steps':
      return ops['set-steps'](p.child, p.name, p.steps.length)
    case 'remove-task':
      return ops['remove-task'](p.child, p.name)
    case 'create-reward':
      return ops['create-reward'](p.name, p.price)
    case 'set-price':
      return ops['set-price'](p.name, p.newPrice, dayName)
  }
}

/** The steps a proposal would leave behind, so a parent reads the actual words. */
function detailOf(proposal: Proposal): readonly string[] {
  if (proposal.op === 'create-task' || proposal.op === 'set-steps') return proposal.steps
  return []
}

export function Assistant({
  session,
  state,
  actor,
  onBack,
}: {
  session: HomeSession
  state: FamilyState
  actor: Actor
  onBack: () => void
}) {
  const [ask, setAsk] = useState('')
  const [token, setToken] = useState(readToken() ?? '')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<AssistantFailure | null>(null)
  const [proposals, setProposals] = useState<readonly Proposal[] | null>(null)
  const [dropped, setDropped] = useState<ReadonlySet<number>>(new Set())
  const [applied, setApplied] = useState(false)
  const abort = useRef<AbortController | null>(null)

  // Dictar es un atajo para llenar el campo de arriba, no una vía aparte. Lo que
  // vuelve de la transcripción es un borrador que se lee y se corrige: whisper
  // falla con los nombres, y los nombres son justo lo que el vocabulario de
  // propuestas casa de forma exacta.
  const [dictando, setDictando] = useState(false)
  const [pasandoATexto, setPasandoATexto] = useState(false)
  const [dictadoNota, setDictadoNota] = useState<DictadoFailure | 'listo' | null>(null)
  const grabacion = useRef<Grabacion | null>(null)

  // Re-planned on every tick, because dropping one change can invalidate a later
  // one that depended on it. The parent always sees the truth of what would
  // happen, not the truth of what would have happened.
  const meetingDay = shortDay(nextMeetingDay(state, session.today()))
  const kept = (proposals ?? []).filter((_, i) => !dropped.has(i))
  const planned =
    proposals === null ? [] : planChanges(state, kept, session.context(actor), session.events())
  const ready = planned.filter((c) => c.error === undefined)
  const refused = planned.filter((c) => c.error !== undefined)

  const send = async (): Promise<void> => {
    setBusy(true)
    setFailure(null)
    setProposals(null)
    setApplied(false)
    setDropped(new Set())
    abort.current = new AbortController()
    try {
      const reply = await askForPlan(assistantConfig(), state, ask, abort.current.signal)
      if (reply.ok) setProposals(reply.proposals)
      else setFailure(reply.failure)
    } finally {
      setBusy(false)
      abort.current = null
    }
  }

  const empezarDictado = async (): Promise<void> => {
    setDictadoNota(null)
    const started = await startDictado(assistantConfig().url, assistantConfig().token)
    if (!started.ok) {
      setDictadoNota(started.failure)
      return
    }
    grabacion.current = started.grabacion
    setDictando(true)
  }

  const terminarDictado = async (): Promise<void> => {
    const actual = grabacion.current
    if (!actual) return
    setDictando(false)
    setPasandoATexto(true)
    try {
      const resultado = await actual.stop()
      if (resultado.ok) {
        // Se añade a lo que ya hubiera escrito, en vez de pisarlo.
        setAsk((antes) =>
          antes.trim().length === 0 ? resultado.texto : `${antes} ${resultado.texto}`,
        )
        setDictadoNota('listo')
      } else {
        setDictadoNota(resultado.failure)
      }
    } finally {
      setPasandoATexto(false)
      grabacion.current = null
    }
  }

  const apply = async (): Promise<void> => {
    // One dispatch for the whole plan, re-run against the live state with the
    // real clock and the real identifiers. The preview above is a preview.
    await session.dispatch(
      (s, c) => ({
        ok: true,
        events: planChanges(s, kept, c, session.events()).flatMap((change) => change.events),
      }),
      actor,
    )
    setApplied(true)
    setProposals(null)
  }

  return (
    <div className="mx-auto max-w-2xl px-5 pt-5 pb-12">
      <button type="button" onClick={onBack} className="text-muted mb-5 text-sm font-bold">
        {es.today.back}
      </button>

      <h1 className="mb-2 text-3xl font-extrabold tracking-tight">{es.assistant.title}</h1>
      <p className="text-muted mt-0 mb-1">{es.assistant.intro}</p>
      <p className="text-muted mt-0 mb-4">{es.assistant.intro2}</p>

      <section className="border-rule bg-surface mb-6 rounded-xl border p-4">
        <p className="m-0 font-bold">{es.assistant.privacyTitle}</p>
        <p className="m-0 mt-2 text-sm">{es.assistant.privacySends}</p>
        <p className="m-0 mt-1 text-sm">{es.assistant.privacyKeeps}</p>
        <p className="text-muted m-0 mt-2 text-sm">{es.assistant.where}</p>
      </section>

      <label htmlFor="ia-ask" className="mb-2 block font-bold">
        {es.assistant.field}
      </label>
      <textarea
        id="ia-ask"
        value={ask}
        rows={6}
        placeholder={es.assistant.placeholder}
        onChange={(e) => {
          setAsk(e.target.value)
        }}
        className="border-rule bg-surface w-full rounded-xl border-2 p-4 text-base"
      />

      <div className="mt-3 flex flex-wrap gap-3">
        {dictando ? (
          <>
            <button
              type="button"
              onClick={() => {
                void terminarDictado()
              }}
              className="bg-coral flex-1 rounded-xl px-6 py-4 text-lg font-bold text-white"
            >
              {es.assistant.stopDictating}
            </button>
            <button
              type="button"
              onClick={() => {
                grabacion.current?.cancel()
                grabacion.current = null
                setDictando(false)
              }}
              className="border-rule rounded-xl border-2 px-5 font-bold"
            >
              {es.assistant.cancelDictating}
            </button>
          </>
        ) : (
          <button
            type="button"
            disabled={busy || pasandoATexto}
            onClick={() => {
              void empezarDictado()
            }}
            className="border-rule rounded-xl border-2 px-5 py-4 font-bold disabled:opacity-40"
          >
            🎙 {es.assistant.dictate}
          </button>
        )}
        <button
          type="button"
          disabled={busy || dictando || pasandoATexto || ask.trim().length === 0}
          onClick={() => {
            void send()
          }}
          className="bg-teal flex-1 rounded-xl px-6 py-4 text-lg font-bold text-white disabled:opacity-40"
        >
          {es.assistant.send}
        </button>
        {busy ? (
          <button
            type="button"
            onClick={() => {
              abort.current?.abort()
            }}
            className="border-rule rounded-xl border-2 px-5 font-bold"
          >
            {es.assistant.cancel}
          </button>
        ) : null}
      </div>

      {dictando ? (
        <p role="status" className="text-coral mt-4 font-bold">
          {es.assistant.recording}
        </p>
      ) : null}

      {pasandoATexto ? (
        <p role="status" className="text-muted mt-4 font-bold">
          {es.assistant.transcribing}
        </p>
      ) : null}

      {dictadoNota === 'listo' ? (
        <p role="status" className="text-mint mt-4 font-bold">
          {es.assistant.dictated}
        </p>
      ) : dictadoNota ? (
        <p role="alert" className="text-coral mt-4 font-bold">
          {es.assistant.dictateFailures[dictadoNota]}
        </p>
      ) : null}

      <p className="text-muted mt-4 text-sm">{es.assistant.dictateWhere}</p>
      <p className="text-muted mt-1 text-sm">{es.assistant.dictateKeeps}</p>

      {busy ? (
        <p role="status" className="text-muted mt-4 font-bold">
          {es.assistant.working}
        </p>
      ) : null}

      {failure ? (
        <p role="alert" className="text-coral mt-4 font-bold">
          {es.assistant.failures[failure]}
        </p>
      ) : null}

      {applied ? (
        <p role="status" className="text-mint mt-4 font-bold">
          {es.assistant.applied}
        </p>
      ) : null}

      {proposals !== null && proposals.length === 0 ? (
        <p className="text-muted mt-4">{es.assistant.empty}</p>
      ) : null}

      {proposals !== null && proposals.length > 0 ? (
        <section className="mt-8">
          <h2 className="mb-1 text-lg font-extrabold">{es.assistant.proposals}</h2>
          <p className="text-muted mt-0 mb-4 text-sm">{es.assistant.review}</p>

          <ul className="grid list-none gap-3 p-0">
            {proposals.map((proposal, index) => {
              // A dropped proposal is not in `planned`, so its verdict comes
              // from position among the kept ones.
              const keptIndex = proposals.slice(0, index).filter((_, i) => !dropped.has(i)).length
              const change = dropped.has(index) ? undefined : planned[keptIndex]
              const refusedHere = change?.error !== undefined
              const steps = detailOf(proposal)
              return (
                <li
                  key={`${proposal.op}-${String(index)}`}
                  className={`border-rule rounded-xl border-2 p-4 ${
                    refusedHere ? 'bg-gold-soft' : 'bg-surface'
                  }`}
                >
                  <label className="flex items-start gap-3 font-bold">
                    <input
                      type="checkbox"
                      className="mt-1 h-6 w-6 shrink-0"
                      checked={!dropped.has(index)}
                      disabled={refusedHere}
                      onChange={() => {
                        setDropped((current) => {
                          const next = new Set(current)
                          if (next.has(index)) next.delete(index)
                          else next.add(index)
                          return next
                        })
                      }}
                    />
                    <span>
                      {describe(change ?? { proposal, events: [], error: undefined }, meetingDay)}
                    </span>
                  </label>
                  {steps.length > 0 ? (
                    <ol className="text-muted mt-2 mb-0 pl-10 text-sm">
                      {steps.map((text, i) => (
                        <li key={`${text}-${String(i)}`}>{text}</li>
                      ))}
                    </ol>
                  ) : null}
                  {refusedHere && change?.error ? (
                    <p className="text-coral m-0 mt-2 pl-9 text-sm font-bold">
                      {es.errors[change.error.code]}
                    </p>
                  ) : null}
                </li>
              )
            })}
          </ul>

          {refused.length > 0 ? (
            <p className="text-muted mt-4 text-sm">{es.assistant.rejectedHelp}</p>
          ) : null}

          <div className="mt-6 flex gap-3">
            <button
              type="button"
              disabled={ready.length === 0}
              onClick={() => {
                void apply()
              }}
              className="bg-teal flex-1 rounded-xl px-6 py-4 text-lg font-extrabold text-white disabled:opacity-40"
            >
              {es.assistant.apply(ready.length)}
            </button>
            <button
              type="button"
              onClick={() => {
                setProposals(null)
              }}
              className="border-rule rounded-xl border-2 px-5 font-bold"
            >
              {es.assistant.discard}
            </button>
          </div>
        </section>
      ) : null}

      <section className="border-rule mt-10 border-t pt-6">
        <label htmlFor="ia-token" className="mb-2 block font-bold">
          {es.assistant.token}
        </label>
        <input
          id="ia-token"
          type="password"
          value={token}
          onChange={(e) => {
            setToken(e.target.value)
          }}
          className="border-rule bg-surface w-full rounded-xl border-2 p-3"
        />
        <p className="text-muted mt-2 mb-3 text-sm">{es.assistant.tokenHelp}</p>
        <button
          type="button"
          onClick={() => {
            writeToken(token)
          }}
          className="border-rule rounded-xl border-2 px-5 py-3 font-bold"
        >
          {es.assistant.saveToken}
        </button>
      </section>
    </div>
  )
}
